import { useCallback, useEffect, FC, useState } from "react";
import { useParams } from "react-router-dom";
import { Loader } from "../../../organisms/common/loading";
import { connectPageService } from "../../../../application/domain/base/base-application-service";
import { ServiceProviderTemplate } from "../../../template/service-provider/default-template";
import { ServiceProviderTabs } from "../../../molecules/service-provider-tab";
import { ServiceProviderR } from "../routes";
import {
  OrgGBizIDSettingContextProps,
  OrgGBizIDSettingService,
} from "../../../../application/service/service-provider/org-gbizid-setting-service";
import { PrimaryButton, Toggle } from "@graffer-inc/console-design-kit";
import { useMount } from "../../../../utils/custom-hooks";

export const OrgGBizIDSettingPage: FC<OrgGBizIDSettingContextProps> = ({
  isLoading,
  organization,
  fetchOrganization,
  updateOrgGBizIDSetting,
}) => {
  const { id: orgId } = useParams<{ id: string }>();
  const [activated, setActivated] = useState(false);
  const [firstUpdate, setFirstUpdate] = useState(false);
  const [enabledUpdatingCredential, setEnabledUpdatingCredential] =
    useState(false);
  const [clientId, setClientId] = useState("");
  const [clientSecret, setClientSecret] = useState("");

  useMount(async () => {
    await fetchOrganization(orgId);
  });

  useEffect(() => {
    if (organization) {
      setActivated(organization.gBizIdSetting?.activated ?? false);
      setClientId(organization?.gBizIdSetting?.clientId ?? "");
      const isFirstUpdate = !organization.gBizIdSetting?.clientId;
      setFirstUpdate(isFirstUpdate);
      // 初期登録時はクレデンシャルの更新が必須のため、firstUpdateをセットしている
      setEnabledUpdatingCredential(isFirstUpdate);
    }
  }, [organization, setActivated, setFirstUpdate]);

  const isValidated = useCallback(() => {
    if (!firstUpdate && !enabledUpdatingCredential) {
      return true;
    }
    return clientId.length > 0 && clientSecret.length > 0;
  }, [firstUpdate, enabledUpdatingCredential, clientSecret, clientId]);

  if (!organization) {
    return null;
  }

  return (
    <ServiceProviderTemplate
      breadcrumbs={[{ to: ServiceProviderR.Home.link({}), title: "Home" }]}
    >
      <div className="has-text-left">
        <div className="columns is-centered">
          <div className="column">
            <ServiceProviderTabs orgId={orgId} />
            <nav className="level">
              <div className="level-left" />
              <div className="level-right">
                <p className="level-item">
                  <PrimaryButton
                    text={"更新する"}
                    disabled={!isValidated() || isLoading}
                    onClick={async () => {
                      if (enabledUpdatingCredential) {
                        await updateOrgGBizIDSetting(
                          orgId,
                          activated,
                          clientId,
                          clientSecret,
                        );
                      } else {
                        await updateOrgGBizIDSetting(orgId, activated);
                      }
                      await fetchOrganization(orgId);
                    }}
                  />
                </p>
              </div>
            </nav>
            <Loader isLoading={isLoading}>
              <div>
                <h3
                  className="title is-size-4 mt-4 mb-4 pl-2"
                  style={{
                    borderLeft: "6px solid #40bfb0",
                  }}
                >
                  GビズID設定
                </h3>
                <div className="field">
                  <Toggle
                    id={"gbizidToggle"}
                    label={"GビズID"}
                    description={
                      "この設定をONにすると、GビズIDでのログインが可能になります。"
                    }
                    checked={activated}
                    onChange={setActivated}
                  />
                </div>
                {!firstUpdate && (
                  <div className="field">
                    <label className="checkbox">
                      <input
                        type="checkbox"
                        checked={enabledUpdatingCredential}
                        onChange={(e) => {
                          setEnabledUpdatingCredential(e.target.checked);
                        }}
                      />
                      クレデンシャルを更新する
                    </label>
                  </div>
                )}
                <div className="field" style={{ width: "350px" }}>
                  <div className="is-small has-name">
                    <label className="label">Client ID</label>
                    <div className="field">
                      {enabledUpdatingCredential ? (
                        <input
                          value={clientId}
                          name="clientId"
                          placeholder=""
                          className="input"
                          onChange={(e) => {
                            setClientId(e.target.value);
                          }}
                        />
                      ) : (
                        <p>{clientId}</p>
                      )}
                    </div>
                  </div>
                </div>
                <div className="field">
                  <div className="is-small has-name">
                    <label className="label">Client Secret</label>
                    <div className="field" style={{ width: "350px" }}>
                      {enabledUpdatingCredential ? (
                        <input
                          type="password"
                          value={clientSecret}
                          name="clientSecret"
                          placeholder=""
                          className="input"
                          onChange={(e) => {
                            setClientSecret(e.target.value);
                          }}
                        />
                      ) : (
                        <p>************</p>
                      )}
                    </div>
                  </div>
                </div>
              </div>
            </Loader>
          </div>
        </div>
      </div>
    </ServiceProviderTemplate>
  );
};

export const OrgGBizIDSettingContainer = connectPageService(
  OrgGBizIDSettingPage,
  () => new OrgGBizIDSettingService(),
);
